import { OrderStore } from "@infrastructure/stores/order.store";
import { OrderClient, CalculateOrderRequest, CalculateOrderResponse, CreateOrderRequest, CreateOrderResponse, OpenCellResponse } from "@infrastructure/clients/http/clients/order.client";
import { OrderStatus } from "@domain/enums/order.enums";
import { OrderHistoryItem, OrderHistoryListItem } from "@domain/dtos/models/order.models";
import { AuthService } from "@domain/services/auth.service";
import { catchError } from "@shared/utils/errors/catchError";
import { userWebSocketClient } from "@infrastructure/clients/ws/clients/user.client";
import { AuthStore } from "@infrastructure/stores/auth.store";
import { trackFunnel } from "@shared/utils/analytics";

const BACKGROUND_POLL_INTERVAL_MS = 10_000; // 10 секунд для фонового опроса истории заказов

class OrderServiceClass {
  private backgroundPollInterval: NodeJS.Timeout | null = null;
  private backgroundPollActive = false;

  getStore() {
    return OrderStore;
  }

  async fetchOrderHistory() {
    // Проверяем авторизацию перед запросом
    const authState = AuthStore.auth;
    const token = AuthService.getToken();
    
    if (authState.state !== "authenticated" || !token) {
      // Пользователь не авторизован или авторизация еще идет, пропускаем запрос
      // Не логируем, чтобы не засорять консоль
      return;
    }

    const orderClient = new OrderClient();
    // Логирование перенесено в RestClient interceptor
    const [error, result] = await catchError(orderClient.getOrderHistory());
    
    if (error) {
      // Ошибка уже обработана через catchError (вызван error.handle())
      // Для фоновых запросов не логируем ошибки, чтобы не засорять консоль
      return;
    }

    if (result) {
      OrderStore.setOrderHistory(result.data);
      // Активные заказы обновляются через WebSocket, не нужно polling
    }
  }

  /**
   * Запускает фоновый опрос истории заказов с заданным интервалом
   * Работает независимо от наличия активных заказов
   */
  startBackgroundPolling() {
    if (this.backgroundPollActive) {
      return; // Уже запущен
    }

    this.backgroundPollActive = true;
    console.log("[OrderService] Starting background polling for order history");
    
    // Первая загрузка сразу
    void this.fetchOrderHistory();

    // Затем опрос каждые 10 секунд
    this.backgroundPollInterval = setInterval(() => {
      // Проверяем авторизацию перед запросом, чтобы не логировать лишнее
      const authState = AuthStore.auth;
      if (authState.state === "authenticated" && AuthService.getToken()) {
        void this.fetchOrderHistory();
      }
    }, BACKGROUND_POLL_INTERVAL_MS);
  }

  /**
   * Останавливает фоновый опрос истории заказов
   */
  stopBackgroundPolling() {
    if (this.backgroundPollInterval) {
      clearInterval(this.backgroundPollInterval);
      this.backgroundPollInterval = null;
      this.backgroundPollActive = false;
      console.log("[OrderService] Stopped background polling");
    }
  }

  // Polling активных заказов удален - они обновляются через WebSocket

  /**
   * Получает заказ по идентификатору
   */
  async getOrderById(orderNumber: string): Promise<OrderHistoryItem> {
    const orderClient = new OrderClient();
    const result = await orderClient.getOrderById(orderNumber);
    return result.data;
  }

  /**
   * Инициализирует глобальное WebSocket соединение для пользователя
   * Вызывается при авторизации пользователя
   */
  initGlobalWebSocket(userId: number): void {
    console.log("[OrderService] Initializing WebSocket connection for user", userId);
    userWebSocketClient.connectToUser(userId).catch((error) => {
      console.error("[OrderService] Failed to connect WebSocket:", error);
    });
  }

  /**
   * Закрывает глобальное WebSocket соединение
   */
  closeGlobalWebSocket(): void {
    console.log("[OrderService] Closing WebSocket connection");
    userWebSocketClient.disconnect();
  }


  addOrder(order: OrderHistoryListItem) {
    OrderStore.addOrder(order);
  }

  updateOrderStatus(orderNumber: string, status: OrderStatus) {
    OrderStore.updateOrderStatus(orderNumber, status);
  }

  /**
   * Рассчитывает финальную стоимость корзины с применением промокода
   */
  async calculateCart(data: CalculateOrderRequest): Promise<CalculateOrderResponse> {
    const orderClient = new OrderClient();
    const [error, result] = await catchError(orderClient.calculateOrder(data));
    
    if (error) {
      throw error;
    }
    
    return result.data;
  }

  /**
   * Рассчитывает финальную стоимость заказа перед созданием
   */
  async calculateOrder(data: CalculateOrderRequest): Promise<CalculateOrderResponse> {
    const orderClient = new OrderClient();
    const [error, result] = await catchError(orderClient.calculateOrder(data));
    
    if (error) {
      throw error;
    }
    
    return result.data;
  }

  /**
   * Создает новый заказ
   */
  async createOrder(data: CreateOrderRequest): Promise<CreateOrderResponse> {
    const orderClient = new OrderClient();
    const [error, result] = await catchError(orderClient.createOrder(data));

    if (error) {
      throw error;
    }

    trackFunnel("order_created", AuthStore.user?.id ?? null, {
      machine_id: data.machine_id,
    });

    return result.data;
  }

  /**
   * Открывает ячейку с готовым заказом.
   * Все готовые заказы этой ячейки переходят в статус issued.
   */
  async openCell(orderNumber: string): Promise<OpenCellResponse> {
    const orderClient = new OrderClient();
    const [error, result] = await catchError(orderClient.openCell(orderNumber));

    if (error) {
      throw error;
    }

    return result.data;
  }
}

export const OrderService = new OrderServiceClass();

